import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/common';
import type { TrashItem } from '@/types/survey.types';

interface TrashModalProps {
  items: TrashItem[];
  onRestore: (id: string) => void;
  onDeleteForever: (id: string) => void;
  onClose: () => void;
}

export function TrashModal({ items, onRestore, onDeleteForever, onClose }: TrashModalProps) {
  const { t } = useTranslation();
  const [confirmItem, setConfirmItem] = useState<TrashItem | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <>
      <div className="trash-modal-overlay open">
        <div className="trash-modal-backdrop" onClick={onClose} />
        <div className="trash-modal-card">
          <div className="trash-modal-header">
            <div className="trash-modal-title-wrap">
              <div className="trash-modal-icon"><i className="fas fa-trash-restore" /></div>
              <div>
                <h3 className="trash-modal-title">{t('admin.trashTitle')}</h3>
                <p className="trash-modal-subtitle">{t('admin.trashSubtitle')}</p>
              </div>
            </div>
            <button className="trash-modal-close" onClick={onClose}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="trash-modal-body">
            {items.length === 0 ? (
              <div className="trash-empty">
                <i className="fas fa-check-circle" />
                <p>{t('admin.trashEmpty')}</p>
              </div>
            ) : (
              <table className="trash-table">
                <thead>
                  <tr>
                    <th>{t('admin.colSurvey')}</th>
                    <th>{t('admin.trashDeletedBy')}</th>
                    <th>{t('admin.trashDeletedAt')}</th>
                    <th>{t('admin.trashTimeLeft')}</th>
                    <th>{t('admin.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const cls = item.daysLeft > 14 ? 'ok' : item.daysLeft > 7 ? 'warn' : 'danger';
                    const daysLabel = item.daysLeft === 0
                      ? t('admin.trashExpiresTODAY')
                      : `${item.daysLeft} ${t('admin.trashDays')}`;
                    return (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.name}</strong>
                          <span className="trash-survey-meta">
                            <i className="fas fa-comment-dots" />
                            {item.responses} {t('admin.trashResponses')}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>{item.deletedBy}</td>
                        <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>{item.deletedAt}</td>
                        <td>
                          <span className={`trash-days-badge ${cls}`}>
                            <i className={`fas ${item.daysLeft === 0 ? 'fa-exclamation-circle' : 'fa-clock'}`} />
                            {' '}{daysLabel}
                          </span>
                        </td>
                        <td>
                          <div className="adm-actions">
                            <button
                              className="btn-icon"
                              title={t('admin.trashRestore')}
                              onClick={() => onRestore(item.id)}
                            >
                              <i className="fas fa-undo" />
                            </button>
                            <button
                              className="btn-icon danger"
                              title={t('admin.confirmDeleteForeverBtn')}
                              onClick={() => setConfirmItem(item)}
                            >
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {confirmItem && (
        <ConfirmModal
          title={t('admin.confirmDeleteForeverTitle')}
          message={t('admin.confirmDeleteForeverMsg')}
          confirmText={t('admin.confirmDeleteForeverBtn')}
          onConfirm={() => onDeleteForever(confirmItem.id)}
          onClose={() => setConfirmItem(null)}
        />
      )}
    </>
  );
}
